/**
 * @fileoverview A gift code opened while signed out, applied once its gardener is signed in.
 *
 * The redeem page (/gift/redeem) keeps the code in this browser when its
 * visitor has no account yet or is signed out (giftCode keepGiftCode), and
 * sends them to sign up or sign in. Once their account is loaded, this
 * applies it, once, and says so in a toast: a year of Grow More until a day,
 * or a year added to their subscription. A used, switched-off or unknown code
 * is said too, and forgotten; a failure without an answer (offline) keeps it
 * for the next visit.
 *
 * Renders nothing. Lives in the app's layout, beside FoundingClaim.
 *
 * Where: mounted once in the Layout, beside FoundingClaim.
 * API: POST /gifts/redeem { code } (redeemGift); then the billing status is
 * read again.
 * Words (en/es): gift.claim.title, gift.claim.notTitle, gift.redeem.done,
 * .doneSubscription, .alreadyYours, .used, .void, .tooFar, .unknown.
 * Watch out: GIFT_FAILED and GIFT_UNAVAILABLE keep the code for the next page
 * load (nothing re-runs the effect sooner); every other refusal forgets it.
 * A failure on our side (no refusal or GIFT_FAILED, with a 500 or more or no
 * answer; D31, lib/serverFailure) is reported to Sentry; nothing is said,
 * as the claim runs unasked and is tried again on the next visit.
 *
 * @module GiftClaim
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useToast } from '@/shared/components/ui/use-toast';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { giftRefusalOf, redeemGift } from '@/shared/api/gifts';
import { BILLING_QUERY_KEY } from '@/features/billing/lib/useBilling';
import { forgetGiftCode, giftDay, keptGiftCode } from '../lib/giftCode';

/** Applies a kept gift code for the signed-in account, once, and says what came of it. */
export const GiftClaim: React.FC = () => {
  const { mysqlUser } = useAuth();
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userId = mysqlUser?.user_id ?? null;
  /** The account a claim was started for, so a re-render does not claim twice. */
  const claimedFor = React.useRef<number | null>(null);

  React.useEffect(() => {
    const code = keptGiftCode();
    if (!userId || !code || claimedFor.current === userId) return;
    claimedFor.current = userId;
    void (async () => {
      try {
        const result = await redeemGift(code);
        forgetGiftCode();
        void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
        const date = giftDay(result.endsAt, language);
        toast({
          title: t('gift.claim.title'),
          description: result.how === 'subscription' ? t('gift.redeem.doneSubscription', { date }) : t('gift.redeem.done', { date }),
        });
      } catch (error) {
        const refusal = giftRefusalOf(error);
        if (!refusal || refusal.code === 'GIFT_FAILED' || refusal.code === 'GIFT_UNAVAILABLE') {
          // Not reached, or it could not be added just now: keep the code and try on the next visit.
          if (refusal?.code !== 'GIFT_UNAVAILABLE' && isServerFailure(error)) reportServerFailure(error, 'Gift claim');
          claimedFor.current = null;
          return;
        }
        forgetGiftCode();
        const description = refusal.code === 'GIFT_USED' && refusal.mine ? t('gift.redeem.alreadyYours')
          : refusal.code === 'GIFT_USED' ? t('gift.redeem.used')
            : refusal.code === 'GIFT_VOID' ? t('gift.redeem.void')
              : refusal.code === 'GIFT_TOO_FAR' ? t('gift.redeem.tooFar') : t('gift.redeem.unknown');
        toast({ title: t('gift.claim.notTitle'), description });
      }
    })();
  }, [userId, queryClient, toast, t, language]);

  return null;
};

export default GiftClaim;
